/**
 * COMET Phase 1 landing page — published at /comet.
 *
 * Mirrors Comet_Phase1_Landing_27JUL2026.pptx. Four stacked sections under the
 * shared NewNavBar + NewFooter:
 *   1. Hero              — phase pill, italicised "European customers" headline, CTA
 *   2. Why list on COMET — 800+ stat plus the three audience cards
 *   3. Expression of Interest — the 3-part form (About You / Portfolio / Data Points)
 *   4. FAQs              — the eight questions from the deck
 *
 * Unlike the rest of the public site (tan/cream), COMET carries its own dark
 * navy + gold identity straight from the deck. Colours are inlined as hex
 * rather than Tailwind tokens because they exist only on this page — adding
 * one-off entries to the shared theme config would imply they are reusable.
 *
 * Validation runs client-side for fast feedback, but the server re-validates
 * every field (comet_service.validate_submission) and is the real gate. When
 * the API rejects a field it returns `field`, which we map straight onto the
 * same error state so the offending input highlights and scrolls into view.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NewNavBar, NavItem, NewFooter } from './NewHome';
import { PrivacyPolicyModal } from './PrivacyPolicyModal';
import { TermsOfServiceModal } from './TermsOfServiceModal';
import LoginModal from './LoginModal';
import SignUpModal from './SignUpModal';
import { InlineAlert } from '../../components/InlineAlert';
import { trackClick } from '../../utils/analytics';
import { apiService } from '../../services/apiService';

// ---------------------------------------------------------------------------
// Palette (from the deck)
// ---------------------------------------------------------------------------
const NAVY = '#153050';
const NAVY_DEEP = '#0E2038';
const NAVY_CARD = '#1B3A5F';
const GOLD = '#B08A2E';
const GOLD_LIGHT = '#C79A2B';
const CREAM = '#F2EFE9';
const MUTED = '#9BAAC0';

// ---------------------------------------------------------------------------
// Static content
// ---------------------------------------------------------------------------
const AUDIENCES = [
  {
    title: 'Corporate R&D decision makers',
    body: 'The people who sign licensing budgets',
  },
  {
    title: 'Innovation ecosystems',
    body: 'Clusters, accelerators and corporate venture arms',
  },
  {
    title: 'Government grant programmes',
    body: 'Non-dilutive funding that pays for first pilots',
  },
];

const ORGANISATION_TYPES = ['Tech Transfer Office', 'Incubator', 'Other'] as const;

interface Stage {
  key: 'pre_revenue' | 'pilots_underway' | 'early_commercial' | 'established_revenue';
  label: string;
  hint: string;
}

const STAGES: Stage[] = [
  { key: 'pre_revenue', label: 'Pre-Revenue', hint: 'lab or prototype, no customers yet' },
  { key: 'pilots_underway', label: 'Pilots Underway', hint: 'trials running, nothing licenced yet' },
  { key: 'early_commercial', label: 'Early Commercial', hint: 'first licence or sale closed' },
  { key: 'established_revenue', label: 'Established Revenue', hint: 'recurring licence or product income' },
];

const DATA_POINTS = [
  { key: 'pricing', label: 'Pricing for Licensing or Sale (range is ok)' },
  { key: 'benchmarking_fto', label: 'Benchmarking & Freedom to Operate' },
  { key: 'price_performance', label: 'Price-Performance & Testing Data' },
  { key: 'trl_by_application', label: 'TRL by Application Area' },
  { key: 'market_sizing', label: 'Market Sizing' },
  { key: 'unsure', label: 'I am UNSURE and Need to Check' },
];

const FAQS = [
  {
    q: 'How is COMET different from existing offerings by UniversityTech.io?',
    a: 'COMET features handpicked, highly curated and validated deep tech, suitable for fast track licensing and global market entry.',
  },
  {
    q: 'Is listing on COMET free?',
    a: 'Yes listing is free for Phase 1. Additional support or services required to make the IP eligible for listing are priced separately.',
  },
  {
    q: 'When will COMET go live?',
    a: 'Phase 1 is expected to go live by September 2026.',
  },
  {
    q: "What is the eligibility criteria for listing on COMET? What if we don't have enough data points?",
    a: 'COMET requires price, price-performance metrics and other data points to ensure readiness. Additional support required to make the IP eligible will be offered, as per need.',
  },
  {
    q: 'I already have conversations or partnerships ongoing with European partners? Do you take exclusive rights?',
    a: 'We do not ask for any exclusivity, and offer an additional viable channel for you to discover and match with suitable partners.',
  },
  {
    q: 'Who sees the technologies that we list? What do they get to see?',
    a: 'In Phase 1, senior consultants across Europe with CXO, entrepreneurial and investment experience, representing key buy side decision makers. Listings on COMET contain only non-confidential information. We help facilitate NDAs and LOIs for pilots.',
  },
  {
    q: 'Do we need IP protection for technologies to be listed on COMET?',
    a: 'IP protection is not mandatory. However, non-confidential disclosed meta information around your know-how/trade secret should be eligible for listing as per COMET criteria.',
  },
  {
    q: 'We are operating in a sensitive technology area of national security. Do we need export clearance to be listed on COMET?',
    a: 'If unsure, we can assist in the required due diligence, as a paid, value added service offering.',
  },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[0-9+\-()\s.]{7,25}$/;

type FormState = 'idle' | 'submitting' | 'success' | 'error';

interface CometPageProps {
  /** Matches RDSymposium / AboutUsNew so the route wiring is interchangeable. */
  onLogin?: (email: string) => void;
}

const CometPage: React.FC<CometPageProps> = ({ onLogin }) => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const formRef = useRef<HTMLDivElement>(null);

  // ---- form state --------------------------------------------------------
  const [form, setForm] = useState({
    work_email: '',
    full_name: '',
    designation: '',
    organisation: '',
    phone: '',
    organisation_type: '',
    organisation_type_other: '',
    pre_revenue: '',
    pilots_underway: '',
    early_commercial: '',
    established_revenue: '',
  });
  const [dataPoints, setDataPoints] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [state, setState] = useState<FormState>('idle');
  const [formError, setFormError] = useState('');
  const [reference, setReference] = useState('');

  useEffect(() => { trackClick('comet_page_view'); }, []);

  const totalOffers = useMemo(
    () => STAGES.reduce((sum, s) => {
      const n = parseInt(form[s.key] || '0', 10);
      return sum + (Number.isFinite(n) && n > 0 ? n : 0);
    }, 0),
    [form.pre_revenue, form.pilots_underway, form.early_commercial, form.established_revenue],
  );

  const setField = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    // Clear this field's error as soon as the user edits it — leaving stale
    // red on a field they are actively fixing reads as unresponsive.
    setErrors(prev => (prev[key] ? { ...prev, [key]: '' } : prev));
    if (state === 'error') { setState('idle'); setFormError(''); }
  };

  const toggleDataPoint = (key: string) => {
    setDataPoints(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
    setErrors(prev => (prev.data_points ? { ...prev, data_points: '' } : prev));
    if (state === 'error') { setState('idle'); setFormError(''); }
  };

  /** Mirrors comet_service.validate_submission. Server still re-checks. */
  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};

    if (!form.work_email.trim()) e.work_email = 'Please enter your work email.';
    else if (!EMAIL_RE.test(form.work_email.trim())) e.work_email = 'Please enter a valid email address.';

    if (form.full_name.trim().length < 2) e.full_name = 'Please enter your full name.';
    if (!form.designation.trim()) e.designation = 'Please enter your designation.';
    if (!form.organisation.trim()) e.organisation = 'Please enter your organisation.';

    if (!form.phone.trim()) e.phone = 'Please enter a phone number.';
    else if (!PHONE_RE.test(form.phone.trim()) || form.phone.replace(/\D/g, '').length < 7) {
      e.phone = 'Please enter a valid phone number.';
    }

    if (!form.organisation_type) e.organisation_type = 'Please select your organisation type.';
    if (form.organisation_type === 'Other' && !form.organisation_type_other.trim()) {
      e.organisation_type_other = 'Please tell us your organisation type.';
    }

    for (const s of STAGES) {
      const raw = form[s.key].trim();
      if (!raw) continue;
      if (!/^\d+$/.test(raw)) e[s.key] = 'Whole numbers only.';
      else if (parseInt(raw, 10) > 10000) e[s.key] = 'That looks too large.';
    }
    if (totalOffers <= 0 && !STAGES.some(s => e[s.key])) {
      e.portfolio = 'Please enter how many technology offers you have in at least one stage.';
    }

    if (dataPoints.length === 0) {
      e.data_points = "Please select at least one data point (or 'I am UNSURE and Need to Check').";
    }
    return e;
  };

  const focusFirstError = (errs: Record<string, string>) => {
    const first = Object.keys(errs).find(k => errs[k]);
    if (!first) return;
    const el = document.querySelector<HTMLElement>(`[data-field="${first}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof (el as HTMLInputElement).focus === 'function') {
        setTimeout(() => (el as HTMLInputElement).focus({ preventScroll: true }), 320);
      }
    }
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.values(errs).some(Boolean)) {
      setState('error');
      setFormError('Please correct the highlighted fields.');
      trackClick('comet_eoi_validation_error', { fields: Object.keys(errs).filter(k => errs[k]).join(',') });
      focusFirstError(errs);
      return;
    }

    setState('submitting');
    setFormError('');
    try {
      const res = await apiService.submitCometInterest({
        ...form,
        pre_revenue: form.pre_revenue || '0',
        pilots_underway: form.pilots_underway || '0',
        early_commercial: form.early_commercial || '0',
        established_revenue: form.established_revenue || '0',
        data_points: dataPoints,
      });

      if (res.success && res.data?.success) {
        setReference(res.data.reference_code || '');
        setState('success');
        trackClick('comet_eoi_submitted', {
          organisation_type: form.organisation_type,
          total_offers: totalOffers,
        });
        window.scrollTo({ top: formRef.current?.offsetTop ? formRef.current.offsetTop - 80 : 0, behavior: 'smooth' });
        return;
      }

      // Field-level rejection from the server → highlight that input.
      const serverField = (res.data as any)?.field;
      const serverError = (res.data as any)?.error || res.error || 'Could not submit your interest. Please try again.';
      if (serverField) {
        const errs2 = { [serverField]: serverError };
        setErrors(errs2);
        focusFirstError(errs2);
      }
      setState('error');
      setFormError(serverError);
      trackClick('comet_eoi_submit_error', { error: serverError, field: serverField || '' });
    } catch (err: any) {
      setState('error');
      setFormError(err?.message || 'Network error. Please check your connection and try again.');
      trackClick('comet_eoi_submit_error', { error: err?.message });
    }
  };

  const scrollToForm = (label: string) => {
    trackClick(label);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ---- shared chrome -----------------------------------------------------
  // Opens the modal rather than calling onLogin directly — onLogin needs the
  // email, which only the modal can supply once the user authenticates.
  const handleLoginClick = () => { trackClick('comet_nav_login'); setShowLoginModal(true); };
  const navItems: NavItem[] = [
    { id: 'about_us', label: 'About Us', href: '#', onClick: () => { window.location.href = '/about'; } },
  ];

  // ---- small presentational helpers --------------------------------------
  const labelCls = 'block text-[13px] tracking-wide mb-1.5';
  const inputBase =
    'w-full bg-transparent border rounded-sm px-3.5 py-2.5 text-[15px] text-white placeholder-[#6E819B] ' +
    'outline-none transition-colors focus:border-[#C79A2B]';

  const inputCls = (field: string) =>
    `${inputBase} ${errors[field] ? 'border-[#E06B6B]' : 'border-[#2C4A6E] hover:border-[#3D5F86]'}`;

  const FieldError: React.FC<{ name: string }> = ({ name }) =>
    errors[name] ? (
      <p className="mt-1 text-[12px] text-[#E9908F]" role="alert">{errors[name]}</p>
    ) : null;

  const Cta: React.FC<{ label: string; track: string; className?: string }> = ({ label, track, className }) => (
    <button
      type="button"
      onClick={() => scrollToForm(track)}
      className={`inline-block px-8 py-4 text-[15px] font-bold text-white transition-colors ${className || ''}`}
      style={{ backgroundColor: GOLD }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = GOLD_LIGHT; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = GOLD; }}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: NAVY }}>
      <NewNavBar
        navItems={navItems}
        onLoginClick={handleLoginClick}
        onLogoClick={() => { window.location.href = '/'; }}
      />

      <main>
        {/* ======================= 1. HERO ======================= */}
        <section className="px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 sm:pb-24" style={{ backgroundColor: NAVY }}>
          <div className="max-w-6xl mx-auto">
            <div className="text-[13px] sm:text-sm font-bold tracking-[0.18em]" style={{ color: CREAM }}>
              COMET by UniversityTech.io
            </div>
            <div className="mt-8 sm:mt-12 text-[12px] sm:text-[13px] tracking-[0.14em]" style={{ color: MUTED }}>
              PHASE 1 OPENS SEPTEMBER 2026
            </div>

            <h1 className="mt-5 text-[34px] leading-[1.15] sm:text-[52px] lg:text-[64px] font-bold text-white">
              Validated deep tech,
              <br className="hidden sm:block" />{' '}
              for <em className="italic font-bold" style={{ color: GOLD }}>European customers.</em>
            </h1>

            <p className="mt-5 text-[16px] sm:text-[19px]" style={{ color: MUTED }}>
              Fast track licensing &amp; market entry, from India with love.
            </p>

            <div className="mt-10 sm:mt-12">
              <Cta label="Express Interest for Phase 1" track="comet_hero_cta" />
            </div>
          </div>
        </section>

        {/* ================= 2. WHY LIST ON COMET ================= */}
        <section className="px-4 sm:px-6 lg:px-8 py-16 sm:py-20" style={{ backgroundColor: NAVY_DEEP }}>
          <div className="max-w-6xl mx-auto">
            <div className="text-[12px] sm:text-[13px] tracking-[0.14em]" style={{ color: MUTED }}>
              WHY LIST YOUR IP/TECH ON COMET
            </div>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <div className="text-[76px] sm:text-[104px] leading-none font-bold" style={{ color: GOLD }}>
                  800+
                </div>
                <p className="mt-4 text-[17px] sm:text-[21px] leading-snug" style={{ color: CREAM }}>
                  senior consultants across Europe with CXO, entrepreneurial or investing background
                </p>
                <div className="mt-8 text-[12px] font-bold tracking-[0.12em] leading-relaxed" style={{ color: GOLD }}>
                  REPRESENTING &amp; ACTIVELY SCOUTING FOR
                </div>
                <div className="mt-10">
                  <Cta label="Express Interest for Phase 1" track="comet_why_cta" />
                </div>
              </div>

              <div className="space-y-4">
                {AUDIENCES.map(a => (
                  <div
                    key={a.title}
                    className="flex items-start gap-4 p-5 sm:p-6"
                    style={{ backgroundColor: NAVY_CARD }}
                  >
                    <span className="mt-1 text-lg select-none" style={{ color: GOLD }} aria-hidden="true">&rarr;</span>
                    <div>
                      <div className="text-[16px] sm:text-[17px] font-bold text-white">{a.title}</div>
                      <div className="mt-1 text-[14px]" style={{ color: MUTED }}>{a.body}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============= 3. EXPRESSION OF INTEREST FORM ============= */}
        <section
          ref={formRef}
          id="express-interest"
          className="px-4 sm:px-6 lg:px-8 py-16 sm:py-20 scroll-mt-20"
          style={{ backgroundColor: NAVY }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-[12px] sm:text-[13px] tracking-[0.14em]" style={{ color: MUTED }}>
              EXPRESSION OF INTEREST
            </div>

            {state === 'success' ? (
              <div className="mt-8 p-8 sm:p-12 text-center" style={{ backgroundColor: NAVY_DEEP }}>
                <div
                  className="mx-auto w-14 h-14 rounded-full flex items-center justify-center text-2xl"
                  style={{ backgroundColor: GOLD, color: NAVY_DEEP }}
                  aria-hidden="true"
                >
                  &#10003;
                </div>
                <h2 className="mt-6 text-[26px] sm:text-[32px] font-bold text-white">Thank you — we have your interest.</h2>
                <p className="mt-3 text-[16px]" style={{ color: CREAM }}>
                  We will revert within 48 hours with next steps and COMET listing eligibility.
                </p>
                {reference && (
                  <div className="mt-8 inline-block px-6 py-4" style={{ backgroundColor: GOLD }}>
                    <div className="text-[12px] tracking-wide" style={{ color: NAVY_DEEP }}>Your tracking reference</div>
                    <div className="text-[22px] font-bold tracking-wider" style={{ color: NAVY_DEEP }}>{reference}</div>
                  </div>
                )}
                <p className="mt-6 text-[13px]" style={{ color: MUTED }}>
                  A confirmation has been emailed to <span className="text-white">{form.work_email}</span>.
                  Please quote your reference in any correspondence.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="mt-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
                  {/* ---------------- 01 About You ---------------- */}
                  <div className="p-6 sm:p-8" style={{ backgroundColor: NAVY_DEEP }}>
                    <div className="text-[15px] font-bold" style={{ color: GOLD }}>01</div>
                    <h2 className="mt-1 text-[24px] sm:text-[27px] font-bold text-white">About You</h2>

                    <div className="mt-6 space-y-4">
                      <div data-field="work_email">
                        <label htmlFor="comet-email" className={labelCls} style={{ color: CREAM }}>
                          Work Email <span style={{ color: GOLD }}>*</span>
                        </label>
                        <input
                          id="comet-email" type="email" autoComplete="email"
                          className={inputCls('work_email')}
                          value={form.work_email}
                          onChange={e => setField('work_email', e.target.value)}
                          aria-invalid={!!errors.work_email}
                        />
                        <FieldError name="work_email" />
                      </div>

                      <div data-field="full_name">
                        <label htmlFor="comet-name" className={labelCls} style={{ color: CREAM }}>
                          Full Name <span style={{ color: GOLD }}>*</span>
                        </label>
                        <input
                          id="comet-name" type="text" autoComplete="name"
                          className={inputCls('full_name')}
                          value={form.full_name}
                          onChange={e => setField('full_name', e.target.value)}
                          aria-invalid={!!errors.full_name}
                        />
                        <FieldError name="full_name" />
                      </div>

                      <div data-field="designation">
                        <label htmlFor="comet-designation" className={labelCls} style={{ color: CREAM }}>
                          Designation <span style={{ color: GOLD }}>*</span>
                        </label>
                        <input
                          id="comet-designation" type="text" autoComplete="organization-title"
                          className={inputCls('designation')}
                          value={form.designation}
                          onChange={e => setField('designation', e.target.value)}
                          aria-invalid={!!errors.designation}
                        />
                        <FieldError name="designation" />
                      </div>

                      <div data-field="organisation">
                        <label htmlFor="comet-org" className={labelCls} style={{ color: CREAM }}>
                          Organisation <span style={{ color: GOLD }}>*</span>
                        </label>
                        <input
                          id="comet-org" type="text" autoComplete="organization"
                          className={inputCls('organisation')}
                          value={form.organisation}
                          onChange={e => setField('organisation', e.target.value)}
                          aria-invalid={!!errors.organisation}
                        />
                        <FieldError name="organisation" />
                      </div>

                      <div data-field="phone">
                        <label htmlFor="comet-phone" className={labelCls} style={{ color: CREAM }}>
                          Phone <span style={{ color: GOLD }}>*</span>
                        </label>
                        <input
                          id="comet-phone" type="tel" autoComplete="tel"
                          className={inputCls('phone')}
                          value={form.phone}
                          onChange={e => setField('phone', e.target.value)}
                          aria-invalid={!!errors.phone}
                        />
                        <FieldError name="phone" />
                      </div>

                      <fieldset data-field="organisation_type" className="pt-1">
                        <legend className={labelCls} style={{ color: CREAM }}>
                          Your organisation is (select one) <span style={{ color: GOLD }}>*</span>
                        </legend>
                        <div className="space-y-2 mt-1">
                          {ORGANISATION_TYPES.map(t => (
                            <label key={t} className="flex items-center gap-2.5 cursor-pointer group">
                              <input
                                type="radio" name="organisation_type" value={t}
                                checked={form.organisation_type === t}
                                onChange={() => setField('organisation_type', t)}
                                className="sr-only peer"
                              />
                              <span
                                className="w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors"
                                style={{ borderColor: form.organisation_type === t ? GOLD : '#3D5F86' }}
                                aria-hidden="true"
                              >
                                {form.organisation_type === t && (
                                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: GOLD }} />
                                )}
                              </span>
                              <span
                                className="text-[15px] transition-colors"
                                style={{ color: form.organisation_type === t ? '#FFFFFF' : CREAM }}
                              >
                                {t}
                              </span>
                            </label>
                          ))}
                        </div>
                        <FieldError name="organisation_type" />

                        {form.organisation_type === 'Other' && (
                          <div className="mt-3" data-field="organisation_type_other">
                            <input
                              type="text" placeholder="Please specify"
                              className={inputCls('organisation_type_other')}
                              value={form.organisation_type_other}
                              onChange={e => setField('organisation_type_other', e.target.value)}
                              aria-label="Please specify your organisation type"
                              aria-invalid={!!errors.organisation_type_other}
                            />
                            <FieldError name="organisation_type_other" />
                          </div>
                        )}
                      </fieldset>
                    </div>
                  </div>

                  {/* ---------------- 02 Your Portfolio ---------------- */}
                  <div className="p-6 sm:p-8" style={{ backgroundColor: NAVY_DEEP }}>
                    <div className="text-[15px] font-bold" style={{ color: GOLD }}>02</div>
                    <h2 className="mt-1 text-[24px] sm:text-[27px] font-bold text-white">Your Portfolio</h2>
                    <p className="mt-2 text-[14px]" style={{ color: MUTED }}>
                      How many <span className="underline">distinct</span> technology offers do you have at each stage?
                    </p>

                    <div className="mt-6 space-y-4" data-field="portfolio">
                      {STAGES.map(s => (
                        <div key={s.key} className="flex items-center justify-between gap-4" data-field={s.key}>
                          <label htmlFor={`comet-${s.key}`} className="min-w-0">
                            <span className="block text-[15px] font-bold text-white">{s.label}</span>
                            <span className="block text-[12.5px] italic" style={{ color: MUTED }}>{s.hint}</span>
                            {errors[s.key] && (
                              <span className="block mt-1 text-[12px] text-[#E9908F]">{errors[s.key]}</span>
                            )}
                          </label>
                          <input
                            id={`comet-${s.key}`}
                            type="text" inputMode="numeric" pattern="[0-9]*"
                            placeholder="0"
                            className={`w-20 shrink-0 text-center bg-transparent border rounded-sm px-2 py-2.5 text-[16px] text-white placeholder-[#4E6280] outline-none transition-colors focus:border-[#C79A2B] ${errors[s.key] ? 'border-[#E06B6B]' : 'border-[#2C4A6E] hover:border-[#3D5F86]'}`}
                            value={form[s.key]}
                            onChange={e => setField(s.key, e.target.value.replace(/[^\d]/g, ''))}
                            aria-invalid={!!errors[s.key]}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 pt-5 flex items-center justify-between" style={{ borderTop: '1px solid #24446B' }}>
                      <span className="text-[13px] font-bold tracking-[0.14em]" style={{ color: GOLD }}>
                        TOTAL OFFERS
                      </span>
                      <span className="text-[26px] font-bold" style={{ color: GOLD }} aria-live="polite">
                        {totalOffers}
                      </span>
                    </div>
                    {errors.portfolio && (
                      <p className="mt-2 text-[12px] text-[#E9908F]" role="alert">{errors.portfolio}</p>
                    )}
                  </div>
                </div>

                {/* ---------------- 03 Available Data Points ---------------- */}
                <div className="mt-5 lg:mt-6 p-6 sm:p-8" style={{ backgroundColor: NAVY_DEEP }} data-field="data_points">
                  <div className="text-[15px] font-bold" style={{ color: GOLD }}>03</div>
                  <h2 className="mt-1 text-[24px] sm:text-[27px] font-bold text-white">Available Data Points</h2>
                  <p className="mt-2 text-[14px] max-w-3xl" style={{ color: MUTED }}>
                    Select the types of data points you have readily available for most of your tech offers.
                    We can support you in case of any gaps, and share further information for Comet listing eligibility.
                  </p>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {DATA_POINTS.map(dp => {
                      const active = dataPoints.includes(dp.key);
                      return (
                        <button
                          key={dp.key} type="button"
                          onClick={() => toggleDataPoint(dp.key)}
                          aria-pressed={active}
                          className="text-left px-4 py-3.5 text-[14px] border transition-colors"
                          style={{
                            borderColor: active ? GOLD : '#3D5F86',
                            backgroundColor: active ? 'rgba(176,138,46,0.14)' : 'transparent',
                            color: active ? '#FFFFFF' : CREAM,
                          }}
                        >
                          <span className="inline-flex items-start gap-2.5">
                            <span
                              className="mt-0.5 w-4 h-4 shrink-0 border flex items-center justify-center text-[11px] font-bold"
                              style={{
                                borderColor: active ? GOLD : '#3D5F86',
                                backgroundColor: active ? GOLD : 'transparent',
                                color: NAVY_DEEP,
                              }}
                              aria-hidden="true"
                            >
                              {active ? '✓' : ''}
                            </span>
                            {dp.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <FieldError name="data_points" />

                  <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4">
                    {state === 'error' && formError && (
                      <div className="sm:mr-auto sm:max-w-md w-full">
                        <InlineAlert variant="error" compact>{formError}</InlineAlert>
                      </div>
                    )}
                    <div className="text-right">
                      <button
                        type="submit"
                        disabled={state === 'submitting'}
                        className="w-full sm:w-auto px-12 py-4 text-[16px] font-bold text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{ backgroundColor: GOLD }}
                      >
                        {state === 'submitting' ? 'SUBMITTING…' : 'SUBMIT'}
                      </button>
                      <div className="mt-2 text-[13px]" style={{ color: MUTED }}>
                        We will revert within 48 hours
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        </section>

        {/* ======================= 4. FAQS ======================= */}
        <section className="px-4 sm:px-6 lg:px-8 py-16 sm:py-20" style={{ backgroundColor: NAVY_DEEP }}>
          <div className="max-w-5xl mx-auto">
            <div className="text-[12px] sm:text-[13px] tracking-[0.14em]" style={{ color: MUTED }}>FAQS</div>
            <div className="mt-8 divide-y" style={{ borderColor: '#24446B' }}>
              {FAQS.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div key={f.q} style={{ borderColor: '#24446B' }} className="border-t first:border-t-0">
                    <button
                      type="button"
                      onClick={() => { setOpenFaq(open ? null : i); if (!open) trackClick('comet_faq_open', { index: i }); }}
                      aria-expanded={open}
                      className="w-full flex items-start gap-4 text-left py-5"
                    >
                      <span className="text-[14px] font-bold shrink-0 mt-0.5" style={{ color: GOLD }}>{i + 1}.</span>
                      <span className="flex-1 text-[16px] sm:text-[17px] font-bold text-white">{f.q}</span>
                      <span
                        className="shrink-0 text-lg transition-transform duration-200"
                        style={{ color: GOLD, transform: open ? 'rotate(45deg)' : 'none' }}
                        aria-hidden="true"
                      >
                        +
                      </span>
                    </button>
                    {open && (
                      <p className="pb-5 pl-8 text-[15px] leading-relaxed" style={{ color: CREAM }}>{f.a}</p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-12 text-center">
              <Cta label="Express Interest for Phase 1" track="comet_faq_cta" />
            </div>
          </div>
        </section>
      </main>

      <NewFooter
        onShowAboutUs={() => { trackClick('comet_footer_about_us'); window.location.href = '/about'; }}
        onShowPrivacy={() => { trackClick('comet_footer_privacy'); setShowPrivacyModal(true); }}
        onShowTerms={() => { trackClick('comet_footer_terms'); setShowTermsModal(true); }}
        onShowRdSymposium={() => { trackClick('comet_footer_rd'); window.location.href = '/rd-symposium'; }}
        onShowWhitePaper={() => { trackClick('comet_footer_whitepaper'); window.location.href = '/rd-symposium'; }}
        onShowUniversities={() => { trackClick('comet_footer_universities'); window.location.href = '/universities'; }}
        onShowComet={() => { trackClick('comet_footer_self'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
      />

      {showPrivacyModal && <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />}
      {showTermsModal && <TermsOfServiceModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />}
      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          onLogin={(email: string) => { setShowLoginModal(false); onLogin?.(email); }}
          onSignUpClick={() => { setShowLoginModal(false); setShowSignUpModal(true); }}
        />
      )}
      {showSignUpModal && (
        <SignUpModal
          onClose={() => setShowSignUpModal(false)}
          onSignupSuccess={() => { setShowSignUpModal(false); window.location.href = '/'; }}
        />
      )}
    </div>
  );
};

export default CometPage;
